import {
  getSubjects, getSubjectVersions,
  getPreviews, getPreviewVersions,
  getHooks, getHookVersions,
  getEmailBodies, getEmailBodyVersions,
  getOffers, getOfferVersions,
  getCtas, getCtaVersions,
  getPsAssets, getPsVersions,
  getLandingPages, getLandingPageVersions,
  getImages, getImageVersions,
  getCsmAssets, getCsmVersions,
  getRinAssets, getRinVersions,
} from "../../../lib/emailMarketingApi";

/**
 * compositionSlots — EMAILS-IMPL-011
 *
 * The 9 governed Creative-composition slots (CRE-SPEC-001 §14's
 * Referenced Asset Requirements table), each wired to its own family's
 * REAL persistent catalog API (EMAILS-IMPL-009A) -- reused unchanged, no
 * second Asset data layer. `required` matches the backend's own
 * REQUIRED_SLOTS/OPTIONAL_SLOTS split (email_marketing_creative_
 * authoring_service.py) exactly: 7 required, P.S./Image optional.
 *
 * `eligibleStatuses` mirrors that same service's own status gate
 * (_ALLOWED_ASSET_STATUS_BY_FAMILY) -- 5-state families (Subject/Preview/
 * Hook/Landing Page/Image) accept Approved/Control; 6-state families
 * (Email Body/Offer/CTA/P.S.) accept Approved/Active. Filtering the
 * frontend selector to these same values means the operator is never
 * offered a choice the backend would reject on submit.
 */
const FIVE_STATE_ELIGIBLE = new Set(["Approved", "Control"]);
const SIX_STATE_ELIGIBLE = new Set(["Approved", "Active"]);

// `backendKey` is the snake_case key the SAME slot uses on the backend's
// own CreativeFingerprint (email_marketing_creatives.py) -- only
// `emailBody`/`landingPage` actually differ from their backend name;
// every other slot's frontend key already matches. Declared explicitly
// per slot (rather than a camelCase-to-snake_case string transform) so
// CompositionSummary and any other fingerprint-reading code never has to
// guess the mapping.
export const COMPOSITION_SLOTS = [
  { key: "subject", backendKey: "subject", label: "Subject", family: "SUB", required: true, idField: "subject_id", listFn: getSubjects, versionsFn: getSubjectVersions, eligibleStatuses: FIVE_STATE_ELIGIBLE },
  { key: "preview", backendKey: "preview", label: "Preview", family: "PRV", required: true, idField: "preview_id", listFn: getPreviews, versionsFn: getPreviewVersions, eligibleStatuses: FIVE_STATE_ELIGIBLE },
  { key: "hook", backendKey: "hook", label: "Hook", family: "HOK", required: true, idField: "hook_id", listFn: getHooks, versionsFn: getHookVersions, eligibleStatuses: FIVE_STATE_ELIGIBLE },
  { key: "emailBody", backendKey: "email_body", label: "Email Body", family: "EML", required: true, idField: "email_body_id", listFn: getEmailBodies, versionsFn: getEmailBodyVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "offer", backendKey: "offer", label: "Offer", family: "OFF", required: true, idField: "offer_id", listFn: getOffers, versionsFn: getOfferVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "cta", backendKey: "cta", label: "CTA", family: "CTA", required: true, idField: "cta_id", listFn: getCtas, versionsFn: getCtaVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "landingPage", backendKey: "landing_page", label: "Landing Page", family: "LPG", required: true, idField: "landing_page_id", listFn: getLandingPages, versionsFn: getLandingPageVersions, eligibleStatuses: FIVE_STATE_ELIGIBLE },
  { key: "ctaSupportMicrocopy", backendKey: "cta_support_microcopy", label: "CTA Support Microcopy", family: "CSM", required: false, idField: "csm_id", listFn: getCsmAssets, versionsFn: getCsmVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "ps", backendKey: "ps", label: "P.S.", family: "PS", required: false, idField: "ps_id", listFn: getPsAssets, versionsFn: getPsVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "replyInstructions", backendKey: "reply_instructions", label: "Reply Instructions", family: "RIN", required: false, idField: "rin_id", listFn: getRinAssets, versionsFn: getRinVersions, eligibleStatuses: SIX_STATE_ELIGIBLE },
  { key: "image", backendKey: "image", label: "Image", family: "IMG", required: false, idField: "image_id", listFn: getImages, versionsFn: getImageVersions, eligibleStatuses: FIVE_STATE_ELIGIBLE },
];

export function emptyComposition() {
  return {
    subject: null, preview: null, hook: null, emailBody: null, offer: null, cta: null,
    landingPage: null, ps: null, image: null, imagePresentation: null,
    ctaSupportMicrocopy: null, replyInstructions: null,
  };
}

/** Converts a backend fingerprint (snake_case, {asset_id, version} per
 * slot) into this frontend's camelCase {assetId, version} shape -- used
 * to prefill "Create New Version" from the Creative's current
 * composition. */
export function compositionFromFingerprint(fingerprint) {
  const slot = (s) => (s ? { assetId: s.asset_id, version: s.version } : null);
  return {
    subject: slot(fingerprint.subject),
    preview: slot(fingerprint.preview),
    hook: slot(fingerprint.hook),
    emailBody: slot(fingerprint.email_body),
    offer: slot(fingerprint.offer),
    cta: slot(fingerprint.cta),
    landingPage: slot(fingerprint.landing_page),
    ps: slot(fingerprint.ps),
    image: slot(fingerprint.image),
    imagePresentation: fingerprint.image_presentation || null,
    ctaSupportMicrocopy: slot(fingerprint.cta_support_microcopy),
    replyInstructions: slot(fingerprint.reply_instructions),
  };
}

export function isCompositionComplete(composition) {
  return COMPOSITION_SLOTS.every((slot) => !slot.required || !!composition[slot.key]);
}

// EMAILS-IMPL-015: the Email Builder's own two disjoint views over the same
// COMPOSITION_SLOTS table -- CRE-SPEC-001 §8.6 draggable Canvas body blocks
// (Hook/Email Body/Offer/CTA/CTA Support Microcopy/P.S./Reply Instructions/
// Image, per the Version 4.7.0/4.8.0 amendments) vs. fixed email-level/
// destination metadata (Subject/Preview/Landing Page), which is never a
// Canvas block. Derived here, once, rather than re-filtered ad hoc in
// Builder components.
const CANVAS_BLOCK_FAMILIES = new Set(["HOK", "EML", "OFF", "CTA", "PS", "IMG", "CSM", "RIN"]);
export const CANVAS_BLOCK_SLOTS = COMPOSITION_SLOTS.filter((slot) => CANVAS_BLOCK_FAMILIES.has(slot.family));
export const FIXED_METADATA_SLOTS = COMPOSITION_SLOTS.filter((slot) => !CANVAS_BLOCK_FAMILIES.has(slot.family));

export const SLOT_KEY_BY_FAMILY = Object.fromEntries(COMPOSITION_SLOTS.map((slot) => [slot.family, slot.key]));
export const SLOT_BY_KEY = Object.fromEntries(COMPOSITION_SLOTS.map((slot) => [slot.key, slot]));

// The single canonical-content field each family's Version response
// carries (email_marketing_asset_authoring_service.ASSET_FAMILY_CONFIGS'
// own response_content_fields) -- Image has none (no single text field;
// its Canvas preview instead uses alt_text/width/height/content_type,
// see EmailBuilderCanvasBlock.jsx).
export const CANONICAL_CONTENT_FIELD_BY_FAMILY = {
  HOK: "canonical_hook",
  EML: "canonical_email_body",
  OFF: "canonical_offer",
  CTA: "canonical_cta",
  PS: "canonical_ps",
  IMG: null,
  CSM: "canonical_csm",
  RIN: "canonical_rin",
};
